import { type FC, useMemo } from "react";
import { type WeightData } from "~/domains/product/types/WeightData";
import {
  type DeliveryEntryData,
  type InvoiceInfoData,
  type ShipmentInformationDataWrapper,
} from "~/domains/orders/types/models";
import { type OrderLineItemData } from "~/domains/orders/types/modelsV2";
import { useTranslation } from "react-i18next";
import { PackageCard, ShipmentContainer } from "~/features/orders/common/components/v2/OrderAndQuoteSharedStyles";
import { Button, Stack } from "~/common/mui";
import { Param } from "~/features/orders/common/components/v2/Params";
import LaunchIcon from "@mui/icons-material/Launch";
import { PackageNumberInformation, PackageWeightInformation } from "./ShipmentPackageInformationV2";
import { ShipmentProduct, ShipmentQuantity } from "./ShipmentProductQuantityV2";

export interface ShippingItem {
  packageNumber: number;
  totalPackages: number;
  packageId: string;
  invoiceData?: InvoiceInfoData;
  weightData: WeightData;
  entriesNumber: number;
  entries: OrderLineItemData[];
}

interface PackageTableProps {
  deliveryEntryData: DeliveryEntryData;
  shipmentInformationDataWrapper: ShipmentInformationDataWrapper;
  "data-test-id"?: string;
}

const PackageTableV2: FC<PackageTableProps> = ({ deliveryEntryData, shipmentInformationDataWrapper }) => {
  const { t } = useTranslation();

  const packageRows: ShippingItem[] = useMemo(() => {
    const packageIds = Object.keys(deliveryEntryData.packages);
    return packageIds.map((key, index) => {
      const packageEntries = shipmentInformationDataWrapper.orderDetailsData[key] || [];
      const packageLines = deliveryEntryData.packages[key] || [];
      const referenceLine = packageLines[0];
      const weightData = referenceLine?.totalWeight ?? packageEntries[0]?.weight ?? deliveryEntryData.weight;
      return {
        packageNumber: index,
        totalPackages: packageIds.length,
        packageId: key,
        invoiceData: packageEntries[0]?.invoiceData,
        weightData,
        entriesNumber: packageEntries.length,
        entries: packageEntries,
      };
    });
  }, [deliveryEntryData.packages, deliveryEntryData.weight, shipmentInformationDataWrapper.orderDetailsData]);

  return (
    <>
      {packageRows &&
        packageRows.map((item: ShippingItem) => (
          <ShipmentContainer sx={{ marginTop: "2px" }} key={item.packageId}>
            <PackageCard>
              <PackageNumberInformation index={item.packageNumber} totalCount={item.totalPackages} />
              <Param
                label={t("orderDetailsPage.table.header.product")}
                value={
                  <Stack gap={0.5}>
                    {item.entries.map((entry) => (
                      <ShipmentProduct key={`${item.packageId}-${entry.lineNo}`} entry={entry} />
                    ))}
                  </Stack>
                }
              />
              <Param
                label={t("cartPage.table.header.quantity.title")}
                value={
                  <Stack gap={0.5} color="text.secondary">
                    {item.entries.map((entry) => (
                      <ShipmentQuantity key={`${item.packageId}-${entry.lineNo}`} entry={entry} />
                    ))}
                  </Stack>
                }
              />
              <PackageWeightInformation weight={item.weightData} />
              {item.invoiceData && (
                <Stack alignItems="flex-start">
                  <Button
                    component="a"
                    href={item.invoiceData?.trackingUrl}
                    variant="text"
                    target="_blank"
                    endIcon={<LaunchIcon />}
                  >
                    {t("orderDetails.page.track.package")}
                  </Button>
                </Stack>
              )}
            </PackageCard>
          </ShipmentContainer>
        ))}
    </>
  );
};

export default PackageTableV2;
